import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiList } from "../../services/list";
import { ContentType, GroupMouseover, GroupPreviewPage, GroupVodPreview, HomepageSectionType, Orientation, } from "../../types/global";
import { useGroupData, useGroupLook } from "../../stores/useGroupStore";
import React from "react";
import { GENERIC_SWIPER } from "../../constants/ui";
import ShowAllLayer from "./ShowAllLayer";
import GenericSwiper, { GenericSwiperSlide, } from "../helpers/GenericSwiper";
import { checkContentLockedForUser, formatContentDuration, formatSchedulingDate, getContentPurchaseTypes, getContentRedirectUrl, sectionToContentType, } from "../../lib/utils";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import useTailwindBreakpoint from "../../hooks/useTailwindBreakpoint";
export default function GenericHomepageSectionSwiper({ data, className, showAllButton, }) {
    const { t } = useTranslation();
    const isLg = useTailwindBreakpoint("lg");
    const groupLook = useGroupLook();
    const groupData = useGroupData();
    // ! la query id può essere non unica
    // TODO: da risolvere
    const slidePaginationResult = useInfiniteQuery({
        queryKey: [
            "homepage-section",
            data.title,
            data.type,
            data.cover_orientation,
        ],
        queryFn: ({ pageParam }) => {
            if (pageParam == 0) {
                return {
                    code: 200,
                    message: "",
                    success: true,
                    content_list: data.content_list,
                    page: 0,
                    total_count_page: 1,
                };
            }
            else {
                return apiList.list.contentList({
                    current_page: pageParam,
                    content_type: sectionToContentType(data.type),
                    parent_id: data.rel,
                    parent_type: data.is_playlist,
                });
            }
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, _, lastPageParam) => lastPage.content_list &&
            lastPage.content_list?.length > 0 &&
            lastPage.total_count_page != lastPage.page
            ? lastPage.page + 1
            : undefined,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        refetchOnMount: true,
    });
    const slideOrientation = data.cover_orientation;
    const contentContextType = sectionToContentType(data.type);
    const mouseoverEnabled = groupLook?.look?.info.mouseover === GroupMouseover.Enabled;
    const previewEnabled = groupLook.look?.info.has_preview_vod_home == GroupVodPreview.Enabled ||
        data.type == HomepageSectionType.ComingSoon;
    const intermediatePage = groupLook.look?.info.preview_page;
    const slides = slidePaginationResult.data?.pages.flatMap((page) => page.content_list?.map((content) => {
        const contentObj = content;
        const image = contentObj.image
            ? JSON.parse(contentObj.image)
            : null;
        const flags = JSON.parse(contentObj.flags);
        const info = typeof contentObj.info === "string"
            ? JSON.parse(contentObj.info)
            : contentObj.info;
        const duration = info?.duration
            ? formatContentDuration(info?.duration, t)
            : null;
        const targetDate = formatSchedulingDate(info?.change_datetime, info?.change_timezone);
        const ageRestriction = info?.age_restriction ?? null;
        const locked = checkContentLockedForUser({ id: content.id, info: info }, groupData.data?.user_data, groupLook.look?.model_data);
        const purchaseTypes = getContentPurchaseTypes({ id: content.id, locked: locked, info: info }, groupData.data?.user_data, groupLook.look?.model_data);
        const redirectUrl = getContentRedirectUrl(locked, content, data.type, slideOrientation, intermediatePage ?? GroupPreviewPage.Disabled, data);
        const contentType = contentContextType == ContentType.ComingSoon
            ? info.true_content_type
            : contentContextType;
        return {
            id: contentObj.id,
            contentType: contentType,
            contentContextType: contentContextType,
            title: contentObj.title,
            description: contentObj.description,
            redirectUrl: redirectUrl,
            image: image ?? undefined,
            flags: flags,
            orientation: slideOrientation,
            ageRestriction: ageRestriction,
            releaseDate: targetDate,
            duration: duration,
            purchaseTypes: purchaseTypes,
        };
    }) ?? []) ?? [];
    const [hovered, setHovered] = React.useState(null);
    const [showAll, setShowAll] = React.useState(false);
    const [showAllHovered, setShowAllHovered] = React.useState(null);
    const setHoveredShowAllSlide = (index) => {
        if (!isLg || !mouseoverEnabled || hovered != null)
            return;
        setShowAllHovered(index);
    };
    const setHoveredSlide = (index) => {
        setHovered(index);
    };
    const handleReachSectionEnd = () => {
        if (slidePaginationResult.hasNextPage &&
            !slidePaginationResult.isFetchingNextPage &&
            slides?.length == GENERIC_SWIPER.PAGE_SLIDE_MAX_COUNT) {
            slidePaginationResult.fetchNextPage();
        }
    };
    return (_jsxs("div", { className: `${className ?? ""} ${slideOrientation == Orientation.Landscape ? "" : "h-full min-h-0"} ${hovered !== null ? "z-50" : "z-10"} relative flex min-w-0 flex-col gap-3 select-none`, children: [showAll && (_jsx(ShowAllLayer, { onClose: () => setShowAll(false), onReachEnd: handleReachSectionEnd, children: slides.map((item, index) => (_jsx(_Fragment, { children: _jsx(GenericSwiperSlide, { data: item, index: index, hoveredSlideIndex: showAllHovered, orientation: slideOrientation, mouseOver: slideOrientation != Orientation.Portrait && mouseoverEnabled, mouseOverPreview: slideOrientation != Orientation.Portrait &&
                            mouseoverEnabled &&
                            previewEnabled, preview: previewEnabled, dataLocation: "bottom", setHoveredSlide: setHoveredShowAllSlide }, index) }))) })), data.title && (_jsx("div", { className: "flex flex-row items-center justify-between gap-3", children: _jsxs("div", { className: `${data.title ? "" : "invisible"} ${slides && slides.length > 3 ? "cursor-pointer" : ""} flex items-center gap-1`, onClick: () => showAllButton && setShowAll(true), children: [_jsx("span", { className: "text-generic text-base md:text-2xl", children: data.title }), slides && slides.length > 3 && showAllButton && _jsx(ChevronRight, {})] }) })), _jsx(GenericSwiper, { data: slides, swiperOrientation: Orientation.Landscape, slideOrientation: slideOrientation, slideConfig: {
                    landscape: {
                        mouseOver: mouseoverEnabled,
                        mouseOverPreview: previewEnabled,
                        preview: previewEnabled,
                        dataLocation: "bottom",
                    },
                    portrait: {
                        mouseOver: mouseoverEnabled,
                        mouseOverPreview: previewEnabled,
                        preview: previewEnabled,
                    },
                }, onHovered: setHoveredSlide, onReachEnd: handleReachSectionEnd })] }));
}
